/*
lightEditor3d: the whole lamp (type, colour, intensity, range, shadows) plus
its program (curveProgram3d), with a power switch. Colour is hue + saturation
with no value slider (value IS intensity). Documented on the b3d-lamp page,
"Editing a lamp"; curve-program's demo uses it.
*/

import { svgElements } from 'tosijs'
import {
  offsetHost,
  label3d,
  slider3d,
  toggle3d,
  select3d,
  type PointerKind,
  type Widget3d,
  type WidgetHost,
} from './widgets3d.js'
import { curveProgram3d } from './curve-program.js'
import { lightPreset, lightPresets } from './light-modulation.js'
import {
  DEFAULT_LIGHT,
  type LightKind,
  type LightSettings,
} from './light-settings.js'
import { stackLayout } from './widgets3d-layout.js'

const { g } = svgElements

export interface LightEditor3dOptions {
  value?: Partial<LightSettings>
  /**
   * A line of guidance above the switch. **Absent by default.**
   *
   * It used to be hard-coded — "flip it to watch the attack and decay" — which
   * was right on the demo page it was written for and wrong everywhere else:
   * teaching copy for OUR docs, shipping inside every consumer's property
   * panel, with no way to turn it off. In an ensemble editor there is no
   * program being watched, there is a lantern in a pirate cove
   * (tosijs-3d#65).
   *
   * The rule it cost us, worth stating: an embedded widget owns the labels of
   * the CONTROLS it draws, and nothing above that. Titles, ids and explanation
   * belong to whoever placed it, because only they know what the thing is
   * called and who is reading.
   */
  hint?: string
  /** Live, including mid-drag — for the lamp itself. */
  handleChange?: (settings: LightSettings) => void
  /** Once per gesture — for a document and one undo step. */
  handleCommit?: (settings: LightSettings, describe: string) => void
}

export interface LightEditorField extends Widget3d {
  readonly value: LightSettings
  setValue: (next: Partial<LightSettings>) => void
}

/**
 * An editor for a whole lamp.
 *
 * ```js
 * const editor = lightEditor3d({
 *   value: { kind: 'spot', hue: 35, intensity: 600 },
 *   handleChange: (s) => applyToLamp(s),
 * })
 * ```
 */
export function lightEditor3d(
  config: LightEditor3dOptions = {}
): LightEditorField {
  let s: LightSettings = { ...DEFAULT_LIGHT, ...config.value }

  const el = g({ 'data-w3d': 'light-editor' })

  const emit = (describe: string, commit: boolean): void => {
    config.handleChange?.({ ...s })
    if (commit) config.handleCommit?.({ ...s }, describe)
  }

  /*
  A slider's gesture end is not exposed, so each one commits on every change.
  That is the honest option today rather than a silent lie: a consumer
  coalescing by `describe` still gets one undo entry per control, and the
  alternative — pretending a drag is one gesture when the widget cannot tell —
  would put the WRONG value in a history.

  Filed as the follow-up rather than worked around here, because the fix
  belongs in `slider3d` where the pointer actually is.
  */
  const num = (
    label: string,
    key: 'intensity' | 'range' | 'angle' | 'hue' | 'saturation',
    min: number,
    max: number,
    step: number,
    extra: {
      scale?: 'linear' | 'log' | 'log2'
      snap?: number
      format?: (v: number) => string
    } = {}
  ) =>
    slider3d({
      label,
      value: s[key],
      min,
      max,
      step,
      showValue: 'always',
      ...extra,
      handleChange: (v) => {
        s = { ...s, [key]: v }
        emit(`set light ${key}`, true)
      },
    })

  const rows: Widget3d[] = [
    // Opt-IN guidance, and nothing when it is absent.
    ...(config.hint ? [label3d({ text: config.hint, muted: true })] : []),
    // The switch FIRST: it is the control you reach for while watching the
    // program, and burying it under six sliders would hide the thing the
    // program editor exists to demonstrate.
    //
    // Its label is just "power". The explanation goes ABOVE as its own line,
    // because a control's label should name the control — a label carrying an
    // instruction reads as part of the sentence and stops looking like a
    // switch, which is how the first version went unrecognised.
    toggle3d({
      label: 'power',
      value: s.on,
      handleChange: (v) => {
        s = { ...s, on: v }
        emit(v ? 'switch light on' : 'switch light off', true)
      },
    }),
    select3d({
      label: 'type',
      value: s.kind,
      options: ['point', 'spot', 'area'],
      handleChange: (v) => {
        s = { ...s, kind: v as LightKind }
        emit('set light type', true)
      },
    }),
    num('hue', 'hue', 0, 360, 1),
    num('saturation', 'saturation', 0, 1, 0.01),
    /*
    LOG, because a lamp's useful range spans five orders of magnitude: a point
    light lives near 1 and a spot near 600, and on a linear 0..1000 track
    everything below 1 sits in the first thousandth of the travel — the values
    you actually reach for are the ones you cannot set.

    Plain decimals rather than significant figures: it is a number people
    compare against other numbers, and "0.05" reads better than "0.0523".

    But the decimals are DROPPED past 100, and that is not cosmetic.
    `showValue: 'always'` reserves the width of the widest value it could
    show, so formatting the top of the range as "1000.00" squeezed the track to
    51px against 81-91px for its neighbours — five decades in fifty pixels,
    which is the same unusable-at-one-end problem the log scale was added to
    fix, arriving from the other direction. "1000" costs three characters less
    and gives the travel back.
    */
    num('intensity', 'intensity', 0.01, 1000, 0, {
      scale: 'log',
      format: (v) => (v >= 100 ? v.toFixed(0) : v.toFixed(2)),
    }),
    num('range', 'range', 1, 60, 1),
    num('cone angle (spot)', 'angle', 5, 160, 1),
    toggle3d({
      label: 'casts shadows',
      value: s.shadows,
      handleChange: (v) => {
        s = { ...s, shadows: v }
        emit(v ? 'enable light shadows' : 'disable light shadows', true)
      },
    }),
    /*
    A section divider, not teaching copy: it names a group of controls this
    widget draws, which is squarely the widget's business. Kept terse for the
    same reason the hint left — a consumer's panel is not our doc page.
    */
    label3d({ text: 'program', muted: true }),
    /*
    A preset PICKER, not a menu of curves to copy: picking one replaces the
    program wholesale and the plots redraw, so it is a starting point you then
    drag rather than a thing you have to accept.
    */
    select3d({
      label: 'preset',
      value: 'steady',
      options: lightPresets.map((p) => ({ label: p.name, value: p.name })),
      handleChange: (name) => {
        const next = lightPreset(String(name))
        if (next == null) return
        s = { ...s, program: next }
        program.setValue(next)
        emit('apply light preset', true)
      },
    }),
  ]

  const program = curveProgram3d({
    value: s.program,
    handleChange: (p) => {
      s = { ...s, program: p }
      config.handleChange?.({ ...s })
    },
    handleCommit: (p, describe) => {
      s = { ...s, program: p }
      config.handleCommit?.({ ...s }, describe)
    },
  })
  rows.push(program)

  for (const r of rows) el.appendChild(r.el)

  let offsets: number[] = []
  let heights: number[] = []
  const GAP = 4
  let captured = -1

  const childAt = (y: number): number => {
    for (let i = 0; i < offsets.length; i++) {
      if (y >= offsets[i] && y <= offsets[i] + heights[i]) return i
    }
    return -1
  }

  return {
    el,

    setHost(host: WidgetHost) {
      // Each child gets the host translated by ITS row offset, so a popup
      // opens beside the control rather than at the top of the editor.
      rows.forEach((r, i) =>
        r.setHost?.(offsetHost(host, () => ({ x: 0, y: offsets[i] ?? 0 })))
      )
    },

    layout(width: number) {
      heights = rows.map((r) => r.layout?.(width) ?? 0)
      const stacked = stackLayout(heights, GAP)
      offsets = stacked.offsets
      rows.forEach((r, i) => {
        r.el.setAttribute('transform', `translate(0, ${offsets[i]})`)
      })
      return stacked.total
    },

    handle(kind: PointerKind, x: number, y: number) {
      if (kind === 'down') captured = childAt(y)
      const i = captured >= 0 ? captured : childAt(y)
      if (i < 0) return
      rows[i].handle?.(kind, x, y - offsets[i])
      if (kind === 'up' || kind === 'leave') captured = -1
    },

    hitTest(x: number, y: number) {
      const i = childAt(y)
      if (i < 0) return false
      return rows[i].hitTest?.(x, y - offsets[i]) ?? true
    },

    get value() {
      return { ...s }
    },

    setValue(next: Partial<LightSettings>) {
      s = { ...s, ...next }
      if (next.program) program.setValue(next.program)
    },
  }
}
